import { useEffect, useState } from "react";
import { getPlans, updateAdminPlan, formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Save, Tag } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

export const AdminPlans = ({ token }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = () => {
    setLoading(true);
    return getPlans()
      .then((p) => setPlans(p))
      .catch(() => toast.error("Errore nel caricamento dei piani"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (slug, fields) => setPlans((prev) => prev.map((x) => (x.slug === slug ? { ...x, ...fields } : x)));

  const save = async (plan) => {
    if (plan.price === "" || Number.isNaN(Number(plan.price))) {
      toast.error("Inserisci un prezzo valido.");
      return;
    }
    setSaving(plan.slug);
    try {
      await updateAdminPlan(token, plan.slug, {
        price: Number(plan.price),
        name: plan.name,
        badge: plan.badge || "",
        highlight: !!plan.highlight,
        description: plan.description || "",
      });
      toast.success(`Piano \"${plan.name}\" aggiornato correttamente.`);
      await load();
    } catch (e) {
      toast.error("Errore nel salvataggio del piano.");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-72 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Modifica i prezzi e i dettagli dei piani. Il prezzo mensile viene ricalcolato automaticamente.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {plans.map((plan) => (
          <Card key={plan.slug} className="rounded-2xl border-border p-5 glow-hover">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Tag className="h-4 w-4" /></span>
                <div>
                  <h3 className="font-semibold leading-tight">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground">{plan.months} mese/i · {formatEUR(plan.price_per_month)}/mese</p>
                </div>
              </div>
              {plan.highlight && <Badge className="rounded-md bg-accent text-accent-foreground">In evidenza</Badge>}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor={`name-${plan.slug}`}>Nome</Label>
                <Input id={`name-${plan.slug}`} value={plan.name} onChange={(e) => patch(plan.slug, { name: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`price-${plan.slug}`}>Prezzo (EUR)</Label>
                <Input
                  id={`price-${plan.slug}`}
                  type="number"
                  min="0"
                  step="1"
                  data-testid={`${ADMIN.planPrice}-${plan.slug}`}
                  value={plan.price}
                  onChange={(e) => patch(plan.slug, { price: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`badge-${plan.slug}`}>Etichetta (badge)</Label>
                <Input id={`badge-${plan.slug}`} value={plan.badge || ""} onChange={(e) => patch(plan.slug, { badge: e.target.value })} placeholder="es. Miglior valore" className="rounded-xl" />
              </div>
              <div className="flex items-end gap-3 pb-1">
                <Switch id={`hl-${plan.slug}`} checked={!!plan.highlight} onCheckedChange={(v) => patch(plan.slug, { highlight: v })} />
                <Label htmlFor={`hl-${plan.slug}`} className="cursor-pointer">Metti in evidenza</Label>
              </div>
            </div>

            <div className="mt-4 space-y-1.5">
              <Label htmlFor={`desc-${plan.slug}`}>Descrizione</Label>
              <Textarea id={`desc-${plan.slug}`} rows={2} value={plan.description || ""} onChange={(e) => patch(plan.slug, { description: e.target.value })} className="rounded-xl" />
            </div>

            <Button
              onClick={() => save(plan)}
              disabled={saving === plan.slug}
              data-testid={`${ADMIN.savePlan}-${plan.slug}`}
              className="mt-5 w-full rounded-xl glow-hover"
            >
              <Save className="h-4 w-4" /> {saving === plan.slug ? "Salvataggio..." : "Salva piano"}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
};
